import { useState, useEffect, useCallback } from 'react';
import { db } from '../../../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from 'firebase/firestore';
import {
  createSurvey,
  updateSurvey,
  deleteSurvey,
  transitionSurveyStatus,
} from '../../../services/surveyService';

/**
 * Hook for survey CRUD operations with real-time updates.
 * Lists, creates, updates, and deletes surveys for an organization.
 *
 * @param {string} organizationId - The organization ID to scope surveys to
 * @returns {object} - surveys, CRUD methods, derived lists, loading/error states
 */
export function useSurveys(organizationId) {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Real-time listener for surveys by organizationId
  useEffect(() => {
    if (!organizationId) {
      setSurveys([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const q = query(
      collection(db, 'Hugo_Surveys'),
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const surveyData = [];
        querySnapshot.forEach((doc) => {
          surveyData.push({ id: doc.id, ...doc.data() });
        });
        setSurveys(surveyData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching surveys:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [organizationId]);

  /**
   * Create a new survey.
   * @param {object} surveyData - Survey fields (title, description, status, targetScope, createdBy, dueDate, questionSet, anonymous)
   * @returns {Promise<object>} - The created survey with id
   */
  const create = useCallback(
    async (surveyData) => {
      if (!organizationId) throw new Error('No organization selected');
      return createSurvey({ ...surveyData, organizationId });
    },
    [organizationId]
  );

  /**
   * Update an existing survey's fields.
   * @param {string} surveyId - The survey document ID
   * @param {object} updates - Fields to update (title, description, status, targetScope, dueDate, questionSet, anonymous)
   * @returns {Promise<void>}
   */
  const update = useCallback(async (surveyId, updates) => {
    if (!surveyId) throw new Error('surveyId is required');
    return updateSurvey(surveyId, updates);
  }, []);

  /**
   * Delete a survey by ID.
   * @param {string} surveyId - The survey document ID
   * @returns {Promise<void>}
   */
  const remove = useCallback(async (surveyId) => {
    if (!surveyId) throw new Error('surveyId is required');
    return deleteSurvey(surveyId);
  }, []);

  /**
   * Transition a survey's status (draft -> active -> closed).
   * @param {string} surveyId - The survey document ID
   * @param {string} newStatus - The target status
   * @returns {Promise<object>} - Updated survey
   */
  const changeStatus = useCallback(async (surveyId, newStatus) => {
    if (!surveyId) throw new Error('surveyId is required');
    return transitionSurveyStatus(surveyId, newStatus);
  }, []);

  // Derived data
  const activeSurveys = surveys.filter((s) => s.status === 'active');
  const draftSurveys = surveys.filter((s) => s.status === 'draft');
  const closedSurveys = surveys.filter((s) => s.status === 'closed');

  return {
    surveys,
    activeSurveys,
    draftSurveys,
    closedSurveys,
    loading,
    error,
    create,
    update,
    remove,
    changeStatus,
  };
}

export default useSurveys;
